import {
  applyNavigation,
  guardDraft,
  polarKind,
  stateCard,
  type DraftCall,
  type NovaHit,
  type NovaSession,
} from './novaSession';
import type { BillingDocument, BillingLineItem } from '../billing/billingService';

export type GeminiPart =
  | { text: string }
  | { functionCall: { name: string; args: Record<string, unknown> } }
  | { functionResponse: { name: string; response: Record<string, unknown> } };

export type GeminiContent = { role: 'user' | 'model'; parts: GeminiPart[] };

export type NovaCall = { name: string; args: Record<string, unknown> };

export type NovaSheet = {
  doc: BillingDocument;
  lines: BillingLineItem[];
  dossierLabel: string | null;
};

export type NovaTurnResult = {
  reply: string;
  session: NovaSession;
  sheet: NovaSheet | null;
};

export type NovaLoopDeps = {
  complete: (input: {
    contents: GeminiContent[];
    state: string;
  }) => Promise<{ text: string; calls: NovaCall[] }>;
  searchDossiers: (query: string) => Promise<NovaHit[]>;
  searchClients: (dossierId: string, query: string) => Promise<NovaHit[]>;
  persistDraft: (session: NovaSession, call: DraftCall) => Promise<{
    session: NovaSession;
    sheet: NovaSheet;
    summary: string;
  }>;
};

const NAV = new Set(['enter_creation_mode', 'exit_creation_mode']);
const DRAFT = new Set([
  'create_quote_draft',
  'save_quote_draft',
  'create_invoice_draft',
  'save_invoice_draft',
]);

export async function runNovaTurn(
  session: NovaSession,
  history: GeminiContent[],
  userText: string,
  deps: NovaLoopDeps,
  sheet: NovaSheet | null,
): Promise<NovaTurnResult> {
  const contents: GeminiContent[] = [
    ...history,
    { role: 'user', parts: [{ text: userText }] },
  ];
  let current = session;
  let paper = sheet;
  let reply = '';

  for (let round = 0; round < 6; round += 1) {
    const turn = await deps.complete({ contents, state: stateCard(current) });
    reply = turn.text.trim();
    if (turn.calls.length === 0) break;

    const responseParts: GeminiPart[] = [];
    contents.push({
      role: 'model',
      parts: turn.calls.map((call) => ({
        functionCall: { name: call.name, args: call.args },
      })),
    });

    for (const call of turn.calls) {
      const outcome = await runCall(current, call, deps);
      current = outcome.session;
      if (outcome.sheet) paper = outcome.sheet;
      responseParts.push({
        functionResponse: {
          name: call.name,
          response: outcome.error ? { error: outcome.error } : { result: outcome.result },
        },
      });
    }
    contents.push({ role: 'user', parts: responseParts });
    reply = '';
  }

  if (!reply) {
    reply = current.creation
      ? 'Je suis sur la feuille. Précise le dossier, le client ou les lignes.'
      : 'Dis-moi si tu veux une facture ou un devis.';
  }
  return { reply, session: current, sheet: current.creation ? paper : null };
}

async function runCall(
  session: NovaSession,
  call: NovaCall,
  deps: NovaLoopDeps,
): Promise<{
  session: NovaSession;
  sheet?: NovaSheet;
  result: Record<string, unknown>;
  error?: string;
}> {
  if (NAV.has(call.name)) {
    const next = applyNavigation(session, call.name, call.args);
    if (next.error) return { session, result: {}, error: next.error };
    return {
      session: next.session,
      result: {
        creation: next.session.creation,
        kind: next.session.kind ? polarKind(next.session.kind) : null,
      },
    };
  }
  if (call.name === 'search_dossiers') {
    const query = String(call.args.query ?? call.args.q ?? '').trim();
    const dossiers = await deps.searchDossiers(query);
    return {
      session: { ...session, dossiers },
      result: { dossiers },
    };
  }
  if (call.name === 'search_clients') {
    const dossierId = String(call.args.dossier_id ?? '').trim();
    if (!session.dossiers.some((d) => d.id === dossierId)) {
      return { session, result: {}, error: 'dossier_id hors du dernier search_dossiers.' };
    }
    const clients = await deps.searchClients(dossierId, String(call.args.query ?? '').trim());
    return {
      session: { ...session, clients, clientDossierId: dossierId },
      result: { clients },
    };
  }
  if (DRAFT.has(call.name)) {
    const guarded = guardDraft(session, call.name, call.args);
    if (!guarded.ok) return { session, result: {}, error: guarded.error };
    const saved = await deps.persistDraft(session, guarded.call);
    return {
      session: saved.session,
      sheet: saved.sheet,
      result: { summary: saved.summary, document_id: saved.session.documentId, polar_id: saved.session.polarId },
    };
  }
  return { session, result: {}, error: `Outil refusé : ${call.name}` };
}
